import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@quiz-commerce/design-system';
import {
  TenantProvider,
  AuthProvider,
  RecommendationProvider,
  ProductProvider,
  ApiStatusProvider,
} from './contexts';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ThemeSwitcherDemo } from './components/ThemeSwitcherDemo';
import { LoadingSpinner } from './components/LoadingSpinner';
import { OfflineBanner } from './components/OfflineBanner';
import { QueryProvider } from './providers/QueryProvider';
import { apiClient } from './api/api-client';
import { logger } from './utils/logger';
// Force rebuild for Railway deployment - 2025-12-03
// Lazy-loaded page imports for code splitting
const QuizPage = lazy(() => import('./pages/QuizPage').then(m => ({ default: m.QuizPage })));
const RecommendationsPage = lazy(() => import('./pages/RecommendationsPage').then(m => ({ default: m.RecommendationsPage })));
const LoginPage = lazy(() => import('./pages/auth').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/auth').then(m => ({ default: m.RegisterPage })));
const ProfilePage = lazy(() => import('./pages/auth').then(m => ({ default: m.ProfilePage })));
const EmailVerificationPage = lazy(() => import('./pages/auth').then(m => ({ default: m.EmailVerificationPage })));
const PasswordResetRequestPage = lazy(() => import('./pages/auth').then(m => ({ default: m.PasswordResetRequestPage })));
const PasswordResetConfirmPage = lazy(() => import('./pages/auth').then(m => ({ default: m.PasswordResetConfirmPage })));
const UnauthorizedPage = lazy(() => import('./pages/auth').then(m => ({ default: m.UnauthorizedPage })));
const ProductCatalogPage = lazy(() => import('./pages/products').then(m => ({ default: m.ProductCatalogPage })));
const ProductDetailPage = lazy(() => import('./pages/products').then(m => ({ default: m.ProductDetailPage })));
const RecommendationDetailPage = lazy(() => import('./pages/recommendations').then(m => ({ default: m.RecommendationDetailPage })));
const PerformanceDashboard = lazy(() => import('./pages/dashboard/performance').then(m => ({ default: m.PerformanceDashboard })));
const CsrfTest = lazy(() => import('./pages/CsrfTest').then(m => ({ default: m.CsrfTest })));

export function App() {
  // Fetch CSRF token on app initialization
  useEffect(() => {
    const CSRF_DEBUG = import.meta.env.DEV || import.meta.env.VITE_CSRF_DEBUG === 'true';

    if (CSRF_DEBUG) {
      logger.log('[CSRF DEBUG] Application mounted, initializing CSRF...');
    }

    const initCsrf = async () => {
      try {
        await apiClient.fetchCsrfToken();
        if (CSRF_DEBUG) {
          logger.log('[CSRF DEBUG] CSRF initialization complete');
        }
      } catch (error) {
        logger.error('[CSRF ERROR] CSRF initialization failed:', error);
      }
    };

    void initCsrf();
  }, []);

  return (
    <ErrorBoundary>
      <ApiStatusProvider>
        <QueryProvider>
          <ThemeProvider tenantId="default">
            <TenantProvider>
              <AuthProvider>
                <ProductProvider>
                  <RecommendationProvider>
                    {/* Skip Navigation Link - WCAG 2.2 Level AA */}
                    <a href="#main-content" className="skip-link">
                      Zum Hauptinhalt springen
                    </a>

                    {/* Offline Banner */}
                    <OfflineBanner />

                    <Suspense fallback={<LoadingSpinner />}>
                        <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<QuizPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/verify-email" element={<EmailVerificationPage />} />
                    <Route path="/reset-password" element={<PasswordResetRequestPage />} />
                    <Route path="/confirm-reset" element={<PasswordResetConfirmPage />} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                    <Route path="/csrf-test" element={<CsrfTest />} />

                    {/* Quiz routes */}
                    <Route path="/quiz" element={<QuizPage />} />

                    {/* Recommendation routes */}
                    <Route path="/recommendations" element={<RecommendationsPage />} />
                    <Route
                      path="/recommendations/:recommendationId"
                      element={<RecommendationDetailPage />}
                    />

                    {/* Product routes */}
                    <Route path="/products" element={<ProductCatalogPage />} />
                    <Route path="/products/:productId" element={<ProductDetailPage />} />

                    {/* Protected routes */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <ProfilePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Dashboard routes */}
                    <Route
                      path="/dashboard/performance"
                      element={
                        <ProtectedRoute>
                          <PerformanceDashboard />
                        </ProtectedRoute>
                      }
                    />

                          {/* Fallback route */}
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>
                    <ThemeSwitcherDemo />
                  </RecommendationProvider>
                </ProductProvider>
              </AuthProvider>
            </TenantProvider>
          </ThemeProvider>
        </QueryProvider>
      </ApiStatusProvider>
    </ErrorBoundary>
  );
}
